<template>
  <v-container fluid v-if="canReadSpareParts">
    <v-card>
      <v-card-title class="text-uppercase">
        {{ title }}
      </v-card-title>
      <v-card-subtitle>
        {{ subtitle }}
      </v-card-subtitle>
      <v-row class="pt-8">
        <v-col class="mr-14 ml-12 mb-6 pr-14 pl-10 ">
          <v-text-field
            v-model="search"
            variant="underlined"
            prepend-icon="mdi-magnify"
            :label="searchBarText"
            class="ma-0 pa-0"
            @keyup.enter="getSparePartsList"
            @click:clear="refreshTable"
            clearable
            :readonly="sparePartsSearchLoading"
            :loading="sparePartsSearchLoading"
          ></v-text-field>
          <v-row class="pl-10 pt-0"
            ><v-radio-group v-model="searchType" inline mandatory>
              <v-radio label="Machines" value="searchTypeMachines"></v-radio>
              <v-radio
                label="Tools"
                value="searchTypeTools"
              ></v-radio> </v-radio-group
          ></v-row>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="mr-16 ml-14 mb-16 pr-16 pl-12">
          <v-data-table
            v-if="tableData.length > 0"
            :headers="headers"
            :items="tableData"
            hide-default-footer
            @click:row="getFileDownload"
            :loading="tableIsLoading"
            loading-text="Loading... Please wait"
            :items-per-page="-1"
          >
            <template v-slot:[`item.icon`]>
              <v-icon>
                mdi-book-open-variant
              </v-icon>
            </template>
            <template v-slot:[`item.downloadIcon`]>
              <v-icon>mdi-download </v-icon>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-snackbar v-model="snackbar" :timeout="3000" location="top" theme="dark" color="error">
        {{ snackbarText }}

        <template v-slot:actions>
          <v-btn variant="text" @click="snackbar = false">
            {{ $t("common.actions.close") }}
          </v-btn>
        </template>
      </v-snackbar>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import fetchClient from "@/utils/fetchClient"

export default {
  data() {
    return {
      title: "SPARE PARTS SEARCH TOOL",
      subtitle: "Search tool for spare parts documents",
      searchBarText:
        "Machine Serial Number, Product Name, Product Name and Revision",
      search: "",
      searchType: "searchTypeMachines",
      tableIsLoading: false,
      snackbar: false
    }
  },
  computed: {
    ...mapGetters([
      "sparePartsList",
      "sparePartsSearchLoading",
      "errorMessage"
    ]),
    canReadSpareParts() {
      return this.$store.getters.canReadSpareParts
    },
    tableData() {
      return this.sparePartsList
    },
    errorMsg() {
      return this.errorMessage
    },
    headers() {
      return [
        {
          title: "",
          align: "end",
          sortable: false,
          key: "icon",
          width: 20
        },
        {
          title: "Filename",
          align: "start",
          sortable: true,
          key: "fileInfo.name"
        },
        {
          title: "Product",
          align: "start",
          sortable: true,
          key: "productId"
        },
        {
          title: "Revision",
          align: "start",
          sortable: true,
          key: "revision"
        },
        {
          title: "Name range",
          align: "start",
          sortable: true,
          key: "serialRange"
        },
        {
          title: "",
          align: "start",
          sortable: false,
          key: "downloadIcon"
        }
      ]
    },
    isTool() {
      if (this.searchType == "searchTypeTools") {
        return true
      }
      return false
    },
    searchSort() {
      let s = this.search

      if (s == null) {
        s = ""
      }

      s = s.toLowerCase()

      let isNum = /^\d+$/.test(s)

      if (this.isTool) {
        return "tool"
      }
      if (isNum && s.length > 5) {
        return "serialNo"
      }
      if (s.includes("rev")) {
        return "prodIdRev"
      }
      return "prodId"
    },
    snackbarText() {
      return this.errorMsg
    }
  },

  methods: {
    ...mapActions(["setError"]),
    async refreshTable() {
      await this.$store.dispatch("clearSparePartsList", {})
    },
    async fetchSparePartsProdIdRev() {
      await this.$store.dispatch("fetchSparePartsProdIdRev", {
        params: this.paramsFromSearchText()
      })
    },
    async fetchSparePartsProdId() {
      await this.$store.dispatch("fetchSparePartsProdId", {
        params: this.paramsFromSearchText()
      })
    },
    async fetchSparePartsSerialNo() {
      await this.$store.dispatch("fetchSparePartsSerialNo", {
        params: this.paramsFromSearchText()
      })
    },
    async fetchSparePartsTools() {
      await this.$store.dispatch("fetchSparePartsTools", {
        params: this.paramsFromSearchText()
      })
    },

    async getSparePartsList() {
      this.snackbar = false
      await this.$store.dispatch("clearErrorMessage", "")

      await this.$store.dispatch("clearSparePartsList", {})

      if (this.searchSort == "serialNo") {
        await this.fetchSparePartsSerialNo()
      }
      if (this.searchSort == "prodIdRev") {
        await this.fetchSparePartsProdIdRev()
      }
      if (this.searchSort == "prodId") {
        await this.fetchSparePartsProdId()
      }
      if (this.searchSort == "tool") {
        await this.fetchSparePartsTools()
      }
    },

    specifyDescaler(s) {
      if (s.includes("descaler")) {
        s = s.replaceAll("descaler", "desc")
        return s
      }
      return s
    },

    paramsFromSearchText() {
      // Lower case to allow input differences

      let s = this.search

      if (s == null) {
        s = ""
      }

      s = s.toLowerCase()
      let searchParams = {}
      let encodedProductId = ""

      s = this.specifyDescaler(s)
      s = s.replaceAll(" ", "")

      if (this.searchSort == "tool") {
        searchParams = { revision: s }
      }
      if (this.searchSort == "serialNo") {
        searchParams = { serialNo: s }
      }
      if (this.searchSort == "prodId") {
        encodedProductId = s.replaceAll("+", "%2B")
        searchParams = { prodId: encodedProductId }
      }

      if (this.searchSort == "prodIdRev") {
        let revStartIndex = s.indexOf("rev")
        let revParam = s.slice(revStartIndex, revStartIndex.length)
        let prodIdParam = s.slice(0, revStartIndex).replaceAll("+", "%2B")
        searchParams = { prodId: prodIdParam, rev: revParam }
      }

      return searchParams
    },
    // On row click download
    async getFileDownload(event, row) {
      this.tableIsLoading = true
      let fileKey = row.item.fileInfo.key
      let fileName = row.item.fileInfo.name

      try {
        const url = `/api/v1/downloads/${fileKey}`
        const blob = await fetchClient().getBlobOrThorw(url)

        let link = document.createElement("a")
        link.href = window.URL.createObjectURL(blob)
        link.download = fileName
        link.click()
      } catch (error) {
        this.setError(error)
      }
      this.tableIsLoading = false
    }
  },
  watch: {
    errorMsg: function() {
      if (this.errorMsg != "") {
        this.snackbar = true
      }
    }
  }
}
</script>
